import { useState } from 'react';
import { s } from '../constants/styles';
import { fmt, todayStr, todayDow, getWeekStart } from '../utils/date';
import { getConsecutiveWeeks, getDayMeta, getDayExercises, getTrainingDayIndices } from '../utils/workout';
import ExerciseProgressCard from './ExerciseProgressCard';
import LineChart from './LineChart';

export default function StatsView({ data, onSaveBodyweight }) {
  const { sessions, bodyweights = [] } = data;
  const [bwStr, setBwStr] = useState("");
  const [bwSaved, setBwSaved] = useState(false);
  const [expandedSession, setExpandedSession] = useState(null);

  const today = todayStr();
  const todayBw = bodyweights.find(b => b.date === today);

  const weeks = getConsecutiveWeeks(sessions);

  // This week's training days
  const weekStart = getWeekStart();
  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  });

  const trainingDayIndices = getTrainingDayIndices(data.dayStructure);
  const weekSessions = weekDates.map((date, i) => {
    const dow = (weekStart.getDay() + i) % 7 || 7;
    const actualDow = (weekStart.getDay() + i) % 7;
    const sess = sessions[date];
    const isTraining = trainingDayIndices.includes(actualDow);
    const exList = getDayExercises(actualDow, data.customProgram, data.dayStructure);
    const done = sess && exList.length > 0 && exList.filter(ex => (sess.exercises?.[ex.id]?.sets || []).filter(s => !s.warmup).length > 0).length >= Math.ceil(exList.length / 2);
    return { date, dow: actualDow, day: getDayMeta(actualDow, data.dayStructure), isTraining, done, hasSess: !!sess };
  });

  // Bodyweight chart
  const bwPoints = [...bodyweights].sort((a, b) => a.date.localeCompare(b.date)).slice(-14).map(b => ({
    y: b.weight, label: fmt(b.date),
  }));

  function logBodyweight() {
    const w = parseFloat(bwStr);
    if (!w) return;
    onSaveBodyweight(w);
    setBwStr("");
    setBwSaved(true);
    setTimeout(() => setBwSaved(false), 2000);
  }

  return (
    <div className="fade-in" style={{ padding: 20, paddingBottom: 100 }}>
      {/* Streak + Weekly grid — combined */}
      <div style={{ ...s.card, ...s.cardGlow, padding: 16, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16, paddingBottom: 14, borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
          <svg width="38" height="38" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
            <path d="M13 2L4.5 13.5H11L10 22L19.5 10.5H13L13 2Z" fill="#fbbf24" stroke="#f59e0b" strokeWidth="1.2" strokeLinejoin="round"/>
          </svg>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <span style={{ fontSize: 26, fontWeight: 800, color: "var(--amber)", lineHeight: 1 }}>{weeks}</span>
              <span style={{ fontSize: 14, color: "var(--text-1)", fontWeight: 700, lineHeight: 1 }}>week{weeks !== 1 ? "s" : ""} straight</span>
            </div>
            <div style={{ fontSize: 11, color: "var(--text-3)" }}>
              {weeks === 0 ? "Log a session to start your streak" : "Keep it going"}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {weekSessions.map((ws, i) => (
            <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: ws.dow === todayDow() ? "var(--accent)" : "var(--text-3)", opacity: ws.isTraining ? 1 : 0.45 }}>
                {ws.day.short}
              </div>
              {ws.isTraining ? (
                <div style={{
                  width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14,
                  background: ws.done ? "rgba(52,211,153,0.15)" : "var(--bg-2)",
                  border: ws.dow === todayDow() ? "1px solid rgba(79,143,255,0.4)" : ws.done ? "1px solid rgba(52,211,153,0.2)" : "1px solid rgba(255,255,255,0.04)",
                }}>
                  {ws.done ? "✓" : <span style={{ width: 6, height: 6, borderRadius: 3, background: "rgba(255,255,255,0.15)", display: "block" }} />}
                </div>
              ) : (
                <div style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ width: 16, height: 1, background: "rgba(255,255,255,0.08)", borderRadius: 1 }} />
                </div>
              )}
              <div style={{ fontSize: 9, fontWeight: 600, textAlign: "center", lineHeight: 1.2,
                color: ws.isTraining ? (ws.done ? "var(--green)" : "var(--text-3)") : "var(--text-3)",
                opacity: ws.isTraining ? 1 : 0.35,
              }}>
                {ws.isTraining ? ws.day.name : "rest"}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Strength + Volume progress */}
      <ExerciseProgressCard sessions={sessions} data={data} />

      {/* Bodyweight */}
      <div style={{ ...s.card, padding: 16, marginBottom: 16 }}>
        <div style={{ ...s.label, marginBottom: 10 }}>BODYWEIGHT</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <input type="number" inputMode="decimal" value={bwStr} onChange={e => setBwStr(e.target.value)}
            placeholder={todayBw ? String(todayBw.weight) : "Today's weight"}
            style={{ ...s.inp, flex: 1, fontSize: 16, padding: "10px 14px" }} />
          <button onClick={logBodyweight} style={{ ...s.btnP, padding: "10px 16px", fontSize: 14, fontFamily: "inherit", flexShrink: 0 }}>
            {bwSaved ? "✓" : "Log"}
          </button>
        </div>
        {bwPoints.length >= 2 ? (
          <LineChart points={bwPoints} color="#a78bfa" unit=" lbs" height={90} />
        ) : (
          <div style={{ fontSize: 12, color: "var(--text-3)", textAlign: "center", padding: "10px 0" }}>
            Log bodyweight daily to see trend
          </div>
        )}
        {todayBw && <div style={{ fontSize: 12, color: "var(--text-3)", textAlign: "center", marginTop: 6 }}>Today: <span style={{ fontWeight: 800, color: "var(--text-1)" }}>{todayBw.weight} lbs</span></div>}
      </div>

      {/* Recent sessions summary */}
      <div style={{ ...s.card, padding: 16 }}>
        <div style={{ ...s.label, marginBottom: 10 }}>RECENT SESSIONS</div>
        {Object.entries(sessions).sort((a, b) => b[0].localeCompare(a[0])).slice(0, 6).map(([date, sess]) => {
          const day = getDayMeta(sess.dayIndex, data.dayStructure);
          if (!day || !day.name) return null;
          const exList = getDayExercises(sess.dayIndex, data.customProgram, data.dayStructure);
          const completed = exList.filter(ex => (sess.exercises?.[ex.id]?.sets || []).filter(s => !s.warmup).length > 0).length;
          const prs = Object.values(sess.exercises || {}).flatMap(ex => (ex.sets || []).filter(s => s.pr)).length;
          const totalVolume = Object.values(sess.exercises || {}).reduce((sum, ex) => {
            return sum + (ex.sets || []).filter(s => !s.warmup).reduce((s2, st) => s2 + st.weight * st.reps, 0);
          }, 0);
          const isExpanded = expandedSession === date;

          return (
            <div key={date} style={{ borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
              {/* Summary row — tap to expand */}
              <button onClick={() => setExpandedSession(isExpanded ? null : date)} style={{
                width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "10px 0", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left",
              }}>
                <div style={{ flex: 1, marginRight: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: day.color || "var(--text-1)" }}>{day.name}</div>
                  <div style={{ fontSize: 11, color: "var(--text-3)" }}>{fmt(date)} · {completed}/{exList.length} exercises</div>
                  {sess.notes?.trim() && <div style={{ fontSize: 11, color: "var(--text-2)", marginTop: 2, fontStyle: "italic" }}>"{sess.notes.trim()}"</div>}
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                  {prs > 0 && <div style={{ fontSize: 11, fontWeight: 800, color: "var(--amber)" }}>🏆 {prs} PR{prs > 1 ? "s" : ""}</div>}
                  {totalVolume > 0 && <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-3)" }}>{totalVolume.toLocaleString()} lbs</div>}
                  {(Array.isArray(sess.cardio) ? sess.cardio : sess.cardio ? [sess.cardio] : []).map((c, i) => (
                    <div key={i} style={{ fontSize: 10, color: "var(--green)" }}>{c.type} {c.duration}m</div>
                  ))}
                  <span style={{ fontSize: 12, color: "var(--text-3)", marginTop: 2 }}>{isExpanded ? "▲" : "▼"}</span>
                </div>
              </button>

              {/* Expanded detail */}
              {isExpanded && (
                <div style={{ paddingBottom: 12 }}>
                  {exList.map(ex => {
                    const exData = sess.exercises?.[ex.id];
                    const ws = (exData?.sets || []).filter(s => !s.warmup);
                    if (!ws.length) return null;
                    const exVolume = ws.reduce((sum, st) => sum + st.weight * st.reps, 0);
                    const exPR = ws.some(s => s.pr);
                    const topSet = ws.reduce((best, st) => !best || st.weight > best.weight ? st : best, null);
                    return (
                      <div key={ex.id} style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "7px 10px", borderRadius: 8, marginBottom: 3,
                        background: exPR ? "rgba(251,191,36,0.05)" : "rgba(255,255,255,0.02)",
                      }}>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-1)" }}>{ex.name}</span>
                            {exPR && <span style={{ fontSize: 9, fontWeight: 800, padding: "1px 5px", borderRadius: 4, background: "var(--amber-dim)", color: "var(--amber)" }}>PR</span>}
                          </div>
                          <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 1 }}>
                            {ws.length} set{ws.length > 1 ? "s" : ""} · {ws.map(st => `${st.weight}×${st.reps}`).join("  ")}
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: day.color, opacity: 0.8 }}>
                            {topSet.weight} lbs
                          </div>
                          <div style={{ fontSize: 10, color: "var(--text-3)" }}>{exVolume.toLocaleString()} vol</div>
                        </div>
                      </div>
                    );
                  })}
                  {(Array.isArray(sess.cardio) ? sess.cardio : sess.cardio ? [sess.cardio] : []).length > 0 && (
                    <div style={{ padding: "6px 10px", borderRadius: 8, background: "rgba(52,211,153,0.05)", marginTop: 4 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--green)", marginBottom: 2 }}>Cardio</div>
                      {(Array.isArray(sess.cardio) ? sess.cardio : [sess.cardio]).map((c, i) => (
                        <div key={i} style={{ fontSize: 12, color: "var(--text-2)" }}>
                          {c.type} · {c.duration}min{c.incline ? ` · ${c.incline}% incline` : ""}{c.speed ? ` · ${c.speed} mph` : ""}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
